import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, proxyImg } from "@/lib/api";
import { Markdown } from "@/components/shared";
import {
  Upload, Trash2, ImageIcon, Plus, X, GripVertical, ArrowUp, ArrowDown,
  Bold, Italic, Heading2, Heading3, List, ListOrdered, Link2, Quote, Image as ImageIco, Eye, Pencil,
} from "lucide-react";

/* ---------------- helpers ---------------- */
export const slugify = (s) =>
  (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export const inputCls =
  "w-full bg-klyroo-bg border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-klyroo-cyan/50 placeholder:text-slate-600";

export function Field({ label, required, hint, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-1.5">
        {label} {required && <span className="text-klyroo-red">*</span>}
      </span>
      {children}
      {hint && <span className="block mt-1 text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

export function TextInput({ value, onChange, ...rest }) {
  return <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} {...rest} />;
}

export function TextArea({ value, onChange, rows = 3, ...rest }) {
  return <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={rows} className={`${inputCls} resize-y`} {...rest} />;
}

export function SelectInput({ value, onChange, options, ...rest }) {
  return (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} {...rest}>
      {options.map((o) => {
        const val = typeof o === "string" ? o : o.value;
        const lab = typeof o === "string" ? o : o.label;
        return <option key={val} value={val}>{lab}</option>;
      })}
    </select>
  );
}

/* ---------------- collapsible section ---------------- */
export function Panel({ title, children, defaultOpen = true, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-white/10 bg-klyroo-bg/40 overflow-hidden" data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/5">
        <span className="text-sm font-display font-bold text-white">{title}</span>
        <span className={`text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
      </button>
      {open && <div className="px-4 pb-4 pt-1 space-y-4">{children}</div>}
    </div>
  );
}

/* ---------------- list of strings (tags, features, pros...) ---------------- */
export function ListInput({ items, onChange, placeholder = "Add item", testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(items || []), v]);
    setDraft("");
  };
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-2 mb-2">
        {(items || []).map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1 text-sm text-slate-200">
            {it}
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-slate-500 hover:text-klyroo-red"><X size={12} /></button>
          </span>
        ))}
        {(!items || items.length === 0) && <span className="text-xs text-slate-600 italic py-1">None yet</span>}
      </div>
      <div className="flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder} className={inputCls} />
        <button type="button" onClick={add} className="shrink-0 inline-flex items-center gap-1 px-3 rounded-lg bg-white/10 text-white text-sm hover:bg-white/20"><Plus size={14} /> Add</button>
      </div>
    </div>
  );
}

/* ---------------- image field (upload + preview + replace/remove) ---------------- */
const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024;

export function MediaField({ value, onChange, folder = "misc", aspect = "16 / 9", testid = "media-field" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) return toast.error("Unsupported type. Use JPG, PNG or WebP.");
    if (file.size > MAX_BYTES) return toast.error("File too large. Max 5 MB.");
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    try {
      const res = await api.adminUpload(form, folder);
      onChange(res.url);
      toast.success("Image uploaded");
    } catch (err) {
      const st = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : (!err.response || (st >= 500 && st < 600))
          ? "The server had a temporary hiccup while uploading. Please try again."
          : "Upload failed";
      toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-start gap-4" data-testid={testid}>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" onChange={handle} className="hidden" data-testid={`${testid}-input`} />
      <div className="shrink-0 overflow-hidden rounded-lg border border-white/10 bg-klyroo-surface flex items-center justify-center" style={{ width: 150, aspectRatio: aspect }}>
        {value ? <img src={proxyImg(value)} alt="preview" data-testid={`${testid}-preview`} className="h-full w-full object-cover" /> : <ImageIcon size={22} className="text-slate-600" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-btn`}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white text-black text-sm font-semibold hover:bg-klyroo-cyan disabled:opacity-60">
            <Upload size={14} /> {busy ? "Uploading…" : value ? "Replace" : "Upload Image"}
          </button>
          {value && <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 text-slate-300 text-sm hover:bg-white/10"><Trash2 size={14} /> Remove</button>}
        </div>
        <p className="mt-2 text-xs text-slate-500">JPG, PNG or WebP · up to 5 MB.</p>
        {value && <p className="mt-1 text-[11px] font-mono text-slate-600 truncate">{value}</p>}
      </div>
    </div>
  );
}

/* ---------------- markdown editor (toolbar + preview) ---------------- */
function LinkDialog({ onInsert, onClose }) {
  const [text, setText] = useState("");
  const [url, setUrl] = useState("https://");
  return (
    <div className="fixed inset-0 z-[130] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md card-surface rounded-xl p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="md-link-dialog">
        <div className="font-display font-bold text-white">Insert link</div>
        <Field label="Link text"><TextInput value={text} onChange={setText} placeholder="e.g. Read the guide" testid="md-link-text" /></Field>
        <Field label="URL (affiliate / tracking params are preserved exactly)"><TextInput value={url} onChange={setUrl} placeholder="https://…" testid="md-link-url" /></Field>
        <div className="flex justify-end gap-2 pt-1">
          <button className="px-3 py-1.5 text-sm text-slate-300 hover:bg-white/5 rounded-lg" onClick={onClose}>Cancel</button>
          <button data-testid="md-link-insert" className="px-4 py-1.5 text-sm bg-klyroo-cyan text-black font-semibold rounded-lg hover:bg-white"
            onClick={() => { if (url) onInsert(`[${text || url}](${url})`); onClose(); }}>Insert</button>
        </div>
      </div>
    </div>
  );
}

function TBtn({ onClick, title, children, testid }) {
  return <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title} data-testid={testid}
    className="p-1.5 rounded text-slate-300 hover:text-white hover:bg-white/10">{children}</button>;
}

export function MarkdownEditor({ value, onChange, minRows = 6, folder = "articles", testid = "md-editor" }) {
  const ref = useRef(null);
  const [tab, setTab] = useState("write");
  const [linkOpen, setLinkOpen] = useState(false);
  const imgRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const v = value || "";

  const surround = (before, after = before) => {
    const el = ref.current;
    const s = el?.selectionStart ?? v.length, e = el?.selectionEnd ?? v.length;
    const sel = v.slice(s, e) || "text";
    const next = v.slice(0, s) + before + sel + after + v.slice(e);
    onChange(next);
    requestAnimationFrame(() => { el?.focus(); el.selectionStart = s + before.length; el.selectionEnd = s + before.length + sel.length; });
  };
  const linePrefix = (prefix) => {
    const el = ref.current;
    const s = el?.selectionStart ?? v.length, e = el?.selectionEnd ?? v.length;
    const start = v.lastIndexOf("\n", s - 1) + 1;
    const block = v.slice(start, e);
    // Strip any existing block prefix so buttons replace (and toggle off) rather than stack.
    const stripRe = /^(\s*)(#{1,3}\s+|>\s?|[-*]\s+|\d+\.\s+)?/;
    const prefixed = block.split("\n").map((l) => {
      const mm = l.match(stripRe);
      const indent = mm ? mm[1] : "";
      const existing = mm && mm[2] ? mm[2] : "";
      const bare = l.slice((mm ? mm[0].length : 0));
      // toggle off if the same prefix is already applied
      if (existing.trim() === prefix.trim()) return indent + bare;
      return indent + prefix + bare;
    }).join("\n");
    onChange(v.slice(0, start) + prefixed + v.slice(e));
    requestAnimationFrame(() => el?.focus());
  };
  const insertAtCursor = (txt) => {
    const el = ref.current;
    const s = el?.selectionStart ?? v.length;
    onChange(v.slice(0, s) + txt + v.slice(s));
  };
  const insert = (md) => insertAtCursor(md);

  const handleImg = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) return toast.error("Use JPG, PNG or WebP.");
    if (file.size > MAX_BYTES) return toast.error("Max 5 MB.");
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const res = await api.adminUpload(form, folder);
      insert(`\n\n![image](${res.url})\n\n`);
      toast.success("Image inserted");
    } catch (err) {
      const st = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : (!err.response || (st >= 500 && st < 600))
          ? "The server had a temporary hiccup while uploading. Please try again."
          : "Upload failed";
      toast.error(msg);
    }
    finally { setUploading(false); }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-klyroo-bg overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-white/10 bg-white/[0.03]">
        <div className="flex items-center gap-0.5 flex-wrap">
          <TBtn onClick={() => surround("**")} title="Bold" testid={`${testid}-bold`}><Bold size={15} /></TBtn>
          <TBtn onClick={() => surround("*")} title="Italic" testid={`${testid}-italic`}><Italic size={15} /></TBtn>
          <TBtn onClick={() => linePrefix("## ")} title="Heading 2" testid={`${testid}-h2`}><Heading2 size={15} /></TBtn>
          <TBtn onClick={() => linePrefix("### ")} title="Heading 3" testid={`${testid}-h3`}><Heading3 size={15} /></TBtn>
          <TBtn onClick={() => linePrefix("- ")} title="Bullet list" testid={`${testid}-ul`}><List size={15} /></TBtn>
          <TBtn onClick={() => linePrefix("1. ")} title="Numbered list" testid={`${testid}-ol`}><ListOrdered size={15} /></TBtn>
          <TBtn onClick={() => linePrefix("> ")} title="Quote" testid={`${testid}-quote`}><Quote size={15} /></TBtn>
          <TBtn onClick={() => setLinkOpen(true)} title="Insert link" testid={`${testid}-link`}><Link2 size={15} /></TBtn>
          <TBtn onClick={() => imgRef.current?.click()} title="Insert image" testid={`${testid}-image`}>{uploading ? <span className="text-[10px] px-1">…</span> : <ImageIco size={15} />}</TBtn>
          <input ref={imgRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImg} className="hidden" />
        </div>
        <div className="flex items-center gap-1 text-xs">
          <button type="button" onClick={() => setTab("write")} data-testid={`${testid}-tab-write`} className={`inline-flex items-center gap-1 px-2 py-1 rounded ${tab === "write" ? "bg-klyroo-cyan text-black" : "text-slate-400 hover:text-white"}`}><Pencil size={12} /> Write</button>
          <button type="button" onClick={() => setTab("preview")} data-testid={`${testid}-tab-preview`} className={`inline-flex items-center gap-1 px-2 py-1 rounded ${tab === "preview" ? "bg-klyroo-cyan text-black" : "text-slate-400 hover:text-white"}`}><Eye size={12} /> Preview</button>
        </div>
      </div>
      {tab === "write" ? (
        <textarea ref={ref} value={v} onChange={(e) => onChange(e.target.value)} rows={minRows}
          data-testid={`${testid}-textarea`} spellCheck
          placeholder="Write here. Use the toolbar for formatting — no need to type Markdown by hand."
          className="w-full bg-transparent px-4 py-3 text-sm text-slate-200 outline-none resize-y leading-relaxed" />
      ) : (
        <div className="px-4 py-3 min-h-[120px]" data-testid={`${testid}-preview`}>
          {v.trim() ? <Markdown content={v} /> : <p className="text-slate-600 italic text-sm">Nothing to preview yet.</p>}
        </div>
      )}
      {linkOpen && <LinkDialog onInsert={insert} onClose={() => setLinkOpen(false)} />}
    </div>
  );
}

/* ---------------- body_sections editor ---------------- */
export function SectionsEditor({ sections, onChange, folder = "articles" }) {
  const list = sections || [];
  const update = (i, patch) => onChange(list.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const copy = [...list];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  return (
    <div className="space-y-3" data-testid="sections-editor">
      {list.map((s, i) => (
        <div key={i} className="rounded-xl border border-white/10 bg-klyroo-bg/40 p-3">
          <div className="flex items-center gap-2 mb-2">
            <GripVertical size={14} className="text-slate-600" />
            <input value={s.heading || ""} onChange={(e) => update(i, { heading: e.target.value })}
              placeholder="Section heading (e.g. What Is …?)" data-testid={`section-heading-${i}`}
              className="flex-1 bg-transparent text-white font-display font-bold text-base outline-none border-b border-transparent focus:border-white/20" />
            <button type="button" onClick={() => move(i, -1)} className="p-1 text-slate-500 hover:text-white"><ArrowUp size={14} /></button>
            <button type="button" onClick={() => move(i, 1)} className="p-1 text-slate-500 hover:text-white"><ArrowDown size={14} /></button>
            <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} data-testid={`section-delete-${i}`} className="p-1 text-slate-500 hover:text-klyroo-red"><Trash2 size={14} /></button>
          </div>
          <MarkdownEditor value={s.content} onChange={(c) => update(i, { content: c })} folder={folder} testid={`section-md-${i}`} />
        </div>
      ))}
      <button type="button" onClick={() => onChange([...list, { heading: "", content: "" }])} data-testid="section-add"
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 text-white text-sm hover:bg-white/10"><Plus size={14} /> Add Section</button>
    </div>
  );
}

/* ---------------- FAQ editor ---------------- */
export function FaqEditor({ faqs, onChange }) {
  const list = faqs || [];
  const update = (i, patch) => onChange(list.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  return (
    <div className="space-y-3" data-testid="faq-editor">
      {list.map((f, i) => (
        <div key={i} className="rounded-xl border border-white/10 bg-klyroo-bg/40 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <input value={f.q || ""} onChange={(e) => update(i, { q: e.target.value })} placeholder="Question" className={inputCls} data-testid={`faq-q-${i}`} />
            <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="p-1 text-slate-500 hover:text-klyroo-red"><Trash2 size={15} /></button>
          </div>
          <TextArea value={f.a} onChange={(a) => update(i, { a })} rows={2} placeholder="Answer" />
        </div>
      ))}
      <button type="button" onClick={() => onChange([...list, { q: "", a: "" }])} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 text-white text-sm hover:bg-white/10"><Plus size={14} /> Add FAQ</button>
    </div>
  );
}

/* ---------------- SEO panel ---------------- */
export function SeoPanel({ f, set, folder }) {
  return (
    <Panel title="SEO Settings (optional)" defaultOpen={false} testid="seo-panel">
      <p className="text-xs text-slate-500 -mt-1">Leave blank to auto-use the title, description and image.</p>
      <Field label="SEO Title"><TextInput value={f.seo_title} onChange={(v) => set("seo_title", v)} placeholder="Custom title for search engines" data-testid="seo-title-input" /></Field>
      <Field label="Meta Description"><TextArea value={f.meta_description} onChange={(v) => set("meta_description", v)} rows={2} placeholder="~155 characters" data-testid="seo-meta-input" /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Focus Keyword"><TextInput value={f.focus_keyword} onChange={(v) => set("focus_keyword", v)} /></Field>
        <Field label="Canonical URL"><TextInput value={f.canonical_url} onChange={(v) => set("canonical_url", v)} placeholder="https://…" /></Field>
      </div>
      <Field label="Social / OG Image"><MediaField value={f.social_image} onChange={(v) => set("social_image", v)} folder={folder} aspect="1.91 / 1" testid="seo-og" /></Field>
    </Panel>
  );
}
